import { useTranslation } from "@sagot/i18n";
import { Badge, Icon, cn, cssVars, type BadgeTone, type IconName } from "@sagot/ui";
import { useState, type ReactNode } from "react";
import { useFormatters, type Formatters } from "../shared/format";
import { CardActions, type CardHandlers } from "./card-actions";
import { CHANNEL_ICONS, displayStatus, normalizeChannels, type Announcement, type DisplayStatus } from "./model";
import { sendsIn } from "./schedule";

const STATUS_BADGE: Readonly<Record<DisplayStatus, { tone: BadgeTone; icon: IconName | null }>> = {
  sent: { tone: "success", icon: null },
  sending: { tone: "success", icon: null },
  scheduled: { tone: "brand", icon: "clock" },
  draft: { tone: "neutral", icon: "pencil" },
  archived: { tone: "neutral", icon: "package" },
};

interface CardProps extends CardHandlers {
  item: Announcement;
  audienceLabel: (codes: readonly number[]) => string;
  authorName: (userId: string | null) => string | null;
}

/** "Jan 4, 2027 · 8:00 AM" — the year only when it differs from the current one. */
function withYear(iso: string | null, fmt: Formatters): string {
  if (!iso) return "";
  const date = new Date(iso);
  const sameYear = date.getFullYear() === new Date().getFullYear();
  const day = new Intl.DateTimeFormat(fmt.locale, { timeZone: fmt.timeZone, month: "short", day: "numeric", year: sameYear ? undefined : "numeric" }).format(date);
  return `${day} · ${fmt.time(iso)}`;
}

function StatusLine({ item, status, authorName }: { item: Announcement; status: DisplayStatus; authorName: CardProps["authorName"] }) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const spec = STATUS_BADGE[status];
  const name = authorName(item.created_by);
  const label = status === "scheduled" ? t("engage.announcements.status.scheduled", { when: withYear(item.scheduled_at, fmt) }) : t(`engage.announcements.status.${status}`);
  const when = status === "draft" ? fmt.date(item.updated_at) : fmt.dateTime(item.sent_at ?? item.created_at);
  let meta: string;
  if (status === "draft") meta = name ? t("engage.announcements.lastEditedBy", { when, name }) : t("engage.announcements.lastEdited", { when });
  else if (status === "scheduled") meta = name ? t("engage.announcements.createdBy", { name }) : "";
  else meta = name ? t("engage.announcements.byline", { when, name }) : t("engage.announcements.bylineNoName", { when });
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Badge tone={spec.tone} data-testid="announcement-status">
        {spec.icon ? <Icon name={spec.icon} className="size-3" /> : <span className="dot" />}
        {label}
      </Badge>
      {item.urgent ? (
        <Badge tone="danger">
          <Icon name="alert-triangle" className="size-3" />
          {t("engage.announcements.urgent")}
        </Badge>
      ) : null}
      {meta ? <span className="text-xs text-fg-subtle">{meta}</span> : null}
    </div>
  );
}

function TeachBadge({ item, status }: { item: Announcement; status: DisplayStatus }) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  if (!item.teach.enabled) {
    return (
      <Badge tone="warning">
        <Icon name="alert-triangle" className="size-3" />
        {t("engage.announcements.notTaught")}
      </Badge>
    );
  }
  const taught = status === "sent" || status === "sending" || status === "archived";
  const label = !taught ? t("engage.announcements.teachesOnSend") : item.teach.expires_at ? t("engage.announcements.taughtExpires", { date: fmt.date(item.teach.expires_at) }) : t("engage.announcements.taught");
  return (
    <Badge tone="brand">
      <Icon name="brain" className="size-3" />
      {label}
    </Badge>
  );
}

function Tile({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="rounded-md bg-surface-2 py-2.5">
      <div className="text-[11px] font-semibold text-fg-subtle">{label}</div>
      <div className="text-base font-extrabold tabular-nums">{children}</div>
    </div>
  );
}

function SentStats({ item }: { item: Announcement }) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const delivered = item.stats.delivered;
  const rate = item.stats.open_rate;
  const pct = `${String(Math.round(rate * 100))}%`;
  return (
    <>
      <div className="grid grid-cols-3 gap-2 text-center">
        <Tile label={t("engage.announcements.delivered")}>{fmt.number(delivered)}</Tile>
        <Tile label={t("engage.announcements.opened")}>{pct}</Tile>
        <Tile label={t("engage.announcements.aiAnswers")}>{fmt.number(item.stats.ai_answers)}</Tile>
      </div>
      <div
        role="img"
        aria-label={t("engage.announcements.openBar", { rate: pct, delivered: fmt.number(delivered) })}
        className="mt-2 flex h-1.5 overflow-hidden rounded-pill bg-sunken"
      >
        <div className="w-[var(--geo-width)] bg-brand-500" style={cssVars({ "--geo-width": pct })} />
      </div>
    </>
  );
}

function ScheduledStats({ item }: { item: Announcement }) {
  const { t } = useTranslation();
  const [now] = useState(Date.now);
  const countdown = item.scheduled_at ? sendsIn(item.scheduled_at, now) : null;
  return (
    <div className="rounded-md border border-dashed border-line-strong p-3 text-xs text-fg-muted">
      <div className="flex items-center justify-between">
        <span>{t("engage.announcements.estimatedReach")}</span>
        <span className="font-bold tabular-nums text-fg">{t("engage.announcements.devices", { count: item.stats.estimated_reach })}</span>
      </div>
      {countdown ? (
        <div className="mt-1.5 flex items-center justify-between">
          <span>{t("engage.announcements.sendsIn")}</span>
          <span className="font-bold text-fg">{t(`engage.announcements.${countdown.unit}`, { count: countdown.count })}</span>
        </div>
      ) : null}
    </div>
  );
}

/** One announcement (mockup `article[data-row]`): content left; stats + actions right on lg. */
export function AnnouncementCard({ item, audienceLabel, authorName, ...handlers }: CardProps) {
  const { t } = useTranslation();
  const status = displayStatus(item.status);
  const sentLike = status === "sent" || status === "sending";
  return (
    <article className={cn("card", status === "draft" && "border-dashed", status === "archived" && "opacity-60")} aria-labelledby={`ann-${item.id}`}>
      <div className="card-body flex flex-col gap-5 lg:flex-row">
        <div className="min-w-0 flex-1">
          <StatusLine item={item} status={status} authorName={authorName} />
          <h2 id={`ann-${item.id}`} className="mt-2 text-base font-bold">
            {item.title}
          </h2>
          <p className="mt-1 whitespace-pre-line text-sm text-fg-muted">{item.body}</p>
          <div className="mt-3 flex flex-wrap items-center gap-1.5 text-xs">
            <Badge>
              <Icon name="users" className="size-3" />
              {audienceLabel(item.audiences)}
              {item.segments.length ? ` (${item.segments.join(", ")})` : ""}
            </Badge>
            {normalizeChannels(item.channels).map((channel) => (
              <Badge key={channel}>
                <Icon name={CHANNEL_ICONS[channel]} className="size-3" />
                {t(`engage.announcements.channels.${channel}`)}
              </Badge>
            ))}
            <TeachBadge item={item} status={status} />
          </div>
        </div>
        <div className={cn("shrink-0 lg:w-80", status === "draft" && "flex flex-col justify-end")}>
          {sentLike ? <SentStats item={item} /> : null}
          {status === "scheduled" ? <ScheduledStats item={item} /> : null}
          <CardActions item={item} status={status} {...handlers} />
        </div>
      </div>
    </article>
  );
}
